/**
 * HustleEventsCatalogPage — public listing of live events.
 *
 * Renders every published event fetched from
 * `GET /api/hustle-events/catalog`, one card per event, plus a CTA to
 * the detail page (which owns the seat picker + Stripe checkout).
 *
 * Global chrome: PlatformHeader + SuiteFooter (module-name token
 * pattern). Every visible string routes through `useTranslation()`
 * so the 40-language bar handles it.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ticket, MapPin, Calendar as CalendarIcon } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function HustleEventsCatalogPage() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "HustleEvents — Upcoming events";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hustle-events/catalog`);
        setEvents(data?.events || []);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [i18n.language]);

  return (
    <div
      data-testid="hustle-events-catalog-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleEvents", to: "/hustle-events" }}
        nav={[
          { to: "/hustle-events", label: t("events_nav_catalog", { defaultValue: "Events" }) },
          { to: "/account", label: t("events_nav_account", { defaultValue: "My tickets" }) },
        ]}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <header className="mb-10">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
            HustleEvents
          </p>
          <h1 className="mt-2 text-3xl md:text-5xl font-black leading-tight">
            {t("events_catalog_title", { defaultValue: "Upcoming events" })}
          </h1>
          <p className="mt-3 text-stone-500 dark:text-stone-400 max-w-xl">
            {t("events_catalog_sub", {
              defaultValue: "Reserve a seat, table or general admission. Your ticket lands in Apple Wallet or Google Wallet automatically.",
            })}
          </p>
        </header>

        {error && (
          <div data-testid="events-catalog-error"
            className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {events === null ? (
          <div data-testid="events-catalog-loading" className="text-stone-400">
            {t("events_catalog_loading", { defaultValue: "Loading events…" })}
          </div>
        ) : events.length === 0 ? (
          <div
            data-testid="events-catalog-empty"
            className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
          >
            <Ticket size={28} className="mx-auto text-amber-500" />
            <p className="mt-4 font-semibold">
              {t("events_catalog_empty_title", { defaultValue: "No events yet." })}
            </p>
            <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
              {t("events_catalog_empty_sub", {
                defaultValue: "New events land here every week — check back soon.",
              })}
            </p>
          </div>
        ) : (
          <div
            data-testid="events-catalog-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {events.map((ev) => (
              <Link
                key={ev.id}
                to={`/hustle-events/${ev.slug || ev.id}`}
                data-testid={`event-card-${ev.slug || ev.id}`}
                className="group rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 hover:border-amber-400 transition"
              >
                <div
                  className="h-40 bg-gradient-to-br from-amber-500 to-orange-600"
                  style={ev.cover_image ? {
                    backgroundImage: `url(${ev.cover_image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  } : undefined}
                />
                <div className="p-5">
                  <h3 className="font-black text-lg group-hover:text-amber-500 transition">
                    {ev.title}
                  </h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-stone-500">
                    {ev.seating_mode}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-sm text-stone-600 dark:text-stone-300">
                    <CalendarIcon size={14} className="text-amber-500" />
                    {fmtWhen(ev.starts_at)}
                  </div>
                  {ev.venue_name && (
                    <div className="mt-1 flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400">
                      <MapPin size={14} className="text-amber-500" />
                      {ev.venue_name}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <SuiteFooter trivagoContext="events" />
    </div>
  );
}
